import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export default function SignUpSidebar() {
  const POST_AUTH_REDIRECT_KEY = "postAuthRedirectPath";
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [emailInput, setEmailInput] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [needsName, setNeedsName] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [creatingUser, setCreatingUser] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);

  // Prevent body scroll when sidebar is open (same behavior as CartSidebar)
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  const getCurrentPath = () =>
    `${window.location.pathname}${window.location.search}${window.location.hash}`;

  const navigateAfterAuth = () => {
    const fallbackPath = getCurrentPath();
    let target = fallbackPath;

    try {
      const savedPath = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
      if (savedPath && savedPath.startsWith("/")) {
        target = savedPath;
      }
      sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
    } catch (e) {}

    navigate(target);
  };

  useEffect(() => {
    const onToggle = (e) => {
      const open = e?.detail?.open ?? !isOpen;

      if (open && e?.detail?.returnToCurrent !== false) {
        try {
          const currentPath =
            e?.detail?.returnPath ||
            getCurrentPath();
          sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, currentPath);
        } catch (err) {}
      }

      if (open) {
        setMounted(true);
        setClosing(false);
        setIsOpen(true);
      } else {
        setClosing(true);
        setIsOpen(false);
        setTimeout(() => setMounted(false), 300);
      }
    };
    window.addEventListener("signup:toggle", onToggle);
    return () => window.removeEventListener("signup:toggle", onToggle);
  }, [isOpen]);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  const handleGoogleLogin = () => {
    const currentPath = getCurrentPath();
    try {
      sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, currentPath);
    } catch (e) {}
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/google?state=${encodeURIComponent(currentPath)}`;
  };

  const sendOtpRequest = async () => {
    if (!emailInput) return setError("Please enter an email");
    setSendingOtp(true);
    setError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/otp/send`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: emailInput }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      setOtpSent(true);
      toast.success("OTP sent to your email");
    } catch (e) {
      console.error(e);
      setError("Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtpRequest = async () => {
    if (!emailInput || !otpInput) return setError("Email and OTP required");
    setVerifyingOtp(true);
    setError("");
    try {
      const body = { email: emailInput, code: otpInput };
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/otp/verify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "OTP verify failed");
      }
      const data = await res.json();
      if (data.needsName) {
        // first-time login: ask for name
        setNeedsName(true);
        setTempToken(data.tempToken);
        toast("Please enter your name to complete sign up");
      } else if (data.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setUser(data.user);
        toast.success("Signed in");
        setClosing(true);
        setIsOpen(false);
        setTimeout(() => setMounted(false), 300);

        // Dispatch login success event so cart can proceed to checkout
        window.dispatchEvent(new Event("auth:login"));
        window.dispatchEvent(new CustomEvent("auth:login-success"));

        // Navigate back to the page that asked for auth (if present), else fallback.
        setTimeout(() => {
          navigateAfterAuth();
        }, 500);
      }
    } catch (e) {
      console.error(e);
      setError("OTP verification failed");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const createUserFromTemp = async () => {
    if (!nameInput) return setError("Name required");
    if (!termsAgreed) {
      toast.error("Please agree with the Terms of Use");
      return;
    }
    if (!newsletterOptIn) {
      toast.error("Please agree to receive newsletters and offers");
      return;
    }
    setCreatingUser(true);
    setError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/otp/create-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tempToken}`,
          },
          body: JSON.stringify({ name: nameInput }),
        },
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Create user failed");
      }
      const data = await res.json();
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setUser(data.user);
      toast.success("Account created and signed in");
      setNeedsName(false);
      setTempToken("");
      setNameInput("");
      setClosing(true);
      setIsOpen(false);
      setTimeout(() => setMounted(false), 300);

      // Dispatch login success event so cart can proceed to checkout
      window.dispatchEvent(new Event("auth:login"));
      window.dispatchEvent(new CustomEvent("auth:login-success"));

      // Navigate back to the page that asked for auth (if present), else fallback.
      setTimeout(() => {
        navigateAfterAuth();
      }, 500);
    } catch (e) {
      console.error(e);
      setError("Failed to create account");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    window.dispatchEvent(new Event("auth:logout"));
    toast.success("Signed out");
    setClosing(true);
    setIsOpen(false);
    //refresh
    setTimeout(() => setMounted(false), 300);
    window.location.reload();
  };

  if (!mounted) return null;

  return (
    <div>
      <div
        className="fixed inset-0 bg-black/50 z-[11000]"
        onClick={() => {
          setClosing(true);
          setIsOpen(false);
          setTimeout(() => setMounted(false), 300);
        }}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 w-full max-w-lg bg-white z-[11001] shadow-2xl flex flex-col drawer-panel ${
          closing ? "drawer-slide-out" : "drawer-slide-in"
        }`}
      >
        <div className="p-2 ">
          <div className="flex items-center justify-between">
            <div className="text-sm text-black font-bdogrotesk">
              {user ? user.name : "Hello Human!"}
            </div>
            <button
              onClick={() => {
                setClosing(true);
                setIsOpen(false);
                setTimeout(() => setMounted(false), 300);
              }}
              className="text-sm text-black font-bdogrotesk"
            >
              Close
            </button>
          </div>
        </div>

        <div className="p-2 flex-1 overflow-y-auto">
          {user ? (
            <div className="">
              <nav className="flex flex-col">
                <button
                  onClick={() => {
                    setClosing(true);
                    setIsOpen(false);
                    setTimeout(() => {
                      setMounted(false);
                      window.dispatchEvent(
                        new CustomEvent("wishlist:toggle", {
                          detail: { open: true },
                        }),
                      );
                    }, 300);
                  }}
                  className="text-left text-6xl md:text-7xl font-bdogrotesk font-medium text-black"
                >
                  Wishlist
                </button>
                <button
                  onClick={() => {
                    setClosing(true);
                    setIsOpen(false);
                    setTimeout(() => setMounted(false), 300);
                    navigate("/orders");
                  }}
                  className="text-left text-6xl md:text-7xl font-bdogrotesk font-medium text-black"
                >
                  Orders
                </button>

                {user && user.role === "admin" && (
                  <button
                    onClick={() => {
                      setClosing(true);
                      setIsOpen(false);
                      setTimeout(() => setMounted(false), 300);
                      navigate("/admin");
                    }}
                    className="text-left text-6xl md:text-7xl font-bdogrotesk font-medium text-black"
                  >
                    Admin
                  </button>
                )}

                <button
                  onClick={() => {
                    setClosing(true);
                    setIsOpen(false);
                    setTimeout(() => setMounted(false), 300);
                    navigate("/contact");
                  }}
                  className="text-left text-6xl md:text-7xl font-bdogrotesk font-medium text-black whitespace-nowrap"
                >
                  Contact Us
                </button>
              </nav>
            </div>
          ) : (
            <div>
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded">
                  {error}
                </div>
              )}

              {needsName ? (
                // First-time signup UI (matches provided mockup)
                <div className="h-full">
                  <h2 className="text-5xl font-bdogrotesk text-black font-semibold mb-6">
                    New here? Let us show you around!
                  </h2>
                  <div className="mb-6">
                    {/* <label className="text-xs text-gray-600">Full Name</label> */}
                    <input
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      className="w-full border-b border-black font-bdogrotesk py-2 focus:outline-none"
                      placeholder="Full Name"
                    />
                  </div>
                </div>
              ) : (
                // Email + OTP UI (matches provided mockup layout)
                <div>
                  <h2 className="text-4xl font-bdogrotesk text-black font-semibold mb-6">
                    You're choosing to shop cultural time capsules, made with
                    Indian hands and human ideas.
                  </h2>
                  <div className="mb-3">
                    {/* <label className="text-xs text-gray-600">Email</label> */}
                    <input
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      className="w-full border-b rounded-none border-black font-bdogrotesk py-2 focus:outline-none"
                      placeholder="Email"
                    />
                  </div>
                  {otpSent && (
                    <div className="mb-3">
                      {/* <label className="text-xs text-gray-600">
                        One Time Password
                      </label> */}
                      <input
                        value={otpInput}
                        onChange={(e) => setOtpInput(e.target.value)}
                        className="w-full border-b border-black rounded-none font-bdogrotesk py-2 focus:outline-none"
                        placeholder="One Time Password"
                      />
                      {/* <div className="text-xs text-gray-400 mt-2">OTP sent!</div> */}
                    </div>
                  )}

                  {/* footer moved out */}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer controls: shown only when not signed in and not in "needsName" flow */}
        {!user && !needsName && (
          <div className="p-2 bg-white pt-4 pb-2 flex flex-col gap-3 text-left z-10">
            <button
              type="button"
              onClick={otpSent ? verifyOtpRequest : sendOtpRequest}
              disabled={sendingOtp || verifyingOtp}
              className="group mt-0 w-full bg-black text-white px-3 py-3 flex items-center justify-between text-[15px] font-semibold tracking-wide box-border"
            >
              <div className="relative h-5 overflow-hidden">
                <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                  <span className="flex font-bdogrotesk h-5 font-medium items-center">
                    {sendingOtp
                      ? "Sending..."
                      : verifyingOtp
                        ? "Verifying..."
                        : otpSent
                          ? "Submit OTP"
                          : "Send OTP"}
                  </span>
                  <span className="flex h-5 font-medium items-center">
                    {sendingOtp
                      ? "Sending..."
                      : verifyingOtp
                        ? "Verifying..."
                        : otpSent
                          ? "Submit OTP"
                          : "Send OTP"}
                  </span>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={handleGoogleLogin}
              style={{
                border: "1px solid #000",
                boxSizing: "border-box",
                backgroundColor: "#fff",
              }}
              className="group mt-0 w-full text-black bg-white px-3 py-3 flex items-center justify-between text-[15px] font-semibold tracking-wide"
            >
              <div className="relative h-5 overflow-hidden">
                <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                  <span className="flex h-5 font-medium items-center">
                    Sign in with Google
                  </span>
                  <span className="flex h-5 font-medium items-center">
                    Sign in with Google
                  </span>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Footer for needsName state: checkboxes and finish button */}
        {!user && needsName && (
          <div className="p-4 bg-white pt-4 pb-2 flex flex-col gap-3 text-left z-10">
            <style>{`
              input[type="checkbox"].custom-checkbox {
                -webkit-appearance: none;
                appearance: none;
                width: 16px;
                height: 16px;
                border: 1px solid #000;
                background: #fff;
                border-radius: 0;
                box-shadow: none;
                display: inline-block;
                vertical-align: middle;
              }
              input[type="checkbox"].custom-checkbox:checked {
                background: #000;
              }
            `}</style>
            <div className="flex flex-col gap-2">
              <label className="flex items-start gap-2 font-bdogrotesk text-sm font-medium">
                <input 
                  type="checkbox" 
                  className="mt-1 custom-checkbox"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                /> I agree with the{" "}
                <a href="/terms" className="underline">
                  Terms of Use
                </a>
              </label>
              <label className="flex items-start gap-2 font-bdogrotesk text-sm font-medium">
                <input 
                  type="checkbox" 
                  className="mt-1 custom-checkbox"
                  checked={newsletterOptIn}
                  onChange={(e) => setNewsletterOptIn(e.target.checked)}
                /> I want to receive occasional,
                thoughtfully sent newsletters with blogs and offers.
              </label>
            </div>
            <button
              type="button"
              onClick={createUserFromTemp}
              disabled={creatingUser}
              className="group mt-0 w-full bg-black text-white px-3 py-3 flex items-center justify-between text-[15px] font-semibold tracking-wide box-border"
            >
              <div className="relative h-5 overflow-hidden">
                <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                  <span className="flex font-bdogrotesk h-5 font-medium items-center">
                    {creatingUser
                      ? "Verifying..."
                      : "Finish Signing Up"}
                  </span>
                  <span className="flex font-bdogrotesk h-5 font-medium items-center">
                    {creatingUser
                      ? "Verifying..."
                      : "Finish Signing Up"}
                  </span>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Sign Out button pinned to bottom for signed-in users (matches wishlist button style) */}
        {user && (
          <div className="p-2 bg-white pt-4 pb-2 flex flex-col gap-3 text-left z-10">
            <button
              type="button"
              onClick={handleLogout}
              style={{
                border: "1px solid #000",
                boxSizing: "border-box",
                backgroundColor: "#fff",
              }}
              className="group mt-0 w-full text-black bg-white px-2 py-2 flex items-center justify-between text-[15px] font-semibold tracking-wide"
            >
              <div className="relative h-5 overflow-hidden">
                <div className="relative font-bdogrotesk text-base flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                  <span className="flex h-5 text-lg items-center">
                    Sign Out
                  </span>
                  <span className="flex h-5 text-lg items-center">
                    Sign Out
                  </span>
                </div>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
